// <s-product-gallery> — gallery-first product carousel (no library, no autoplay).
// Markup: thumbnail buttons [data-gallery-thumb] (command="--go", data-index), a stage (data-rail selector) of
// [data-gallery-slide] items, previous/next buttons (command="--prev" / "--next", hidden until this runs) and a
// [data-gallery-counter]. Once defined, CSS stacks the slides in one cell; this element only sets state:
// data-active + data-side on slides (CSS owns the opacity/drift/scale transition and its reduced-motion override),
// inert on hidden slides so focus never lands on an invisible image, aria-current on thumbnails, aria-disabled
// on the step buttons at either end, and the counter text. Input: thumbnails, buttons, arrow keys anywhere in the
// gallery, and horizontal swipe or mouse drag on the stage (vertical page scroll stays native via touch-action).
const SWIPE_DISTANCE = 40;

class ProductGallery extends HTMLElement {
  #controller = null;
  #rail = null;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #pointer = null;
  #suppressClick = false;

  connectedCallback() {
    this.#rail = this.querySelector(this.dataset.rail || '[data-product-gallery]');
    if (!this.#rail) return;
    this.#slides = [...this.#rail.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (this.#slides.length < 2) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.#rail.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
    this.#rail.addEventListener('pointerup', (event) => this.#onPointerUp(event), { signal });
    this.#rail.addEventListener('pointercancel', () => { this.#pointer = null; }, { signal });
    // Capture phase: a swipe or drag that ends on an image must not also open the zoom dialog.
    this.#rail.addEventListener('click', (event) => {
      if (!this.#suppressClick) return;
      this.#suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });

    this.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((button) => { button.hidden = false; });
    const initial = Number(this.dataset.initialIndex);
    this.#setActive(Number.isInteger(initial) && initial >= 0 && initial < this.#slides.length ? initial : 0, false);
    this.#watchVariant();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  // Variant media sync: the product form (data-variant-source) re-renders on every shade change and carries
  // [data-variant-media-id] = the selected variant's first linked image. Show that image when it changes.
  #observer = null;
  #variantMediaId = '';

  #watchVariant() {
    const source = this.dataset.variantSource ? document.querySelector(this.dataset.variantSource) : null;
    if (!source) return;
    this.#variantMediaId = source.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
    this.#observer = new MutationObserver(() => {
      const mediaId = source.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
      if (!mediaId || mediaId === this.#variantMediaId) return;
      this.#variantMediaId = mediaId;
      const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index >= 0) this.#go(index);
    });
    // Attribute changes too: s-variant-picker.js sets data-variant-media-id directly on a swatch click.
    this.#observer.observe(source, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-variant-media-id'] });
  }

  #onCommand(event) {
    if (event.command === '--next') this.#go(this.#index + 1);
    else if (event.command === '--prev') this.#go(this.#index - 1);
    else if (event.command === '--go') this.#go(Number(event.source?.dataset.index));
  }

  #onKeydown(event) {
    if (event.target.closest('input, textarea, select')) return;
    const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown';
    // Up/Down only steps while focus is on a thumbnail (the vertical rail); elsewhere they keep scrolling the page.
    if (vertical && !event.target.matches('[data-gallery-thumb]')) return;
    if (!vertical && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const forward = vertical ? event.key === 'ArrowDown' : (event.key === 'ArrowRight') === !this.#rtl();
    const next = this.#go(this.#index + (forward ? 1 : -1));
    if (next === null) return;
    event.preventDefault();
    // Keep keyboard focus on the control the shopper is using; a step button that just became disabled hands focus to the stage's live slide.
    if (event.target.matches('[data-gallery-thumb]')) this.#thumbs[next]?.focus();
  }

  #onPointerDown(event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    this.#pointer = { x: event.clientX, y: event.clientY };
  }

  #onPointerUp(event) {
    if (!this.#pointer) return;
    const dx = event.clientX - this.#pointer.x;
    const dy = event.clientY - this.#pointer.y;
    this.#pointer = null;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    const forward = (dx < 0) === !this.#rtl();
    this.#suppressClick = true;
    // Reset in case the browser fires no click after the drag.
    setTimeout(() => { this.#suppressClick = false; }, 400);
    this.#go(this.#index + (forward ? 1 : -1));
  }

  #go(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.#slides.length || index === this.#index) return null;
    this.#setActive(index, true);
    return index;
  }

  #setActive(index, announce) {
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === index);
      slide.dataset.side = i < index ? 'before' : 'after';
      slide.inert = i !== index;
    });
    this.#thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));

    const counter = this.querySelector('[data-gallery-counter]');
    const current = counter?.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(index + 1);
    const live = counter?.querySelector('[data-gallery-live]');
    if (live && announce) live.textContent = (counter.dataset.label || '').replace('[i]', String(index + 1));

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    const focused = document.activeElement;
    this.#setDisabled(prev, index === 0);
    this.#setDisabled(next, index === this.#slides.length - 1);
    // A disabled end button turns invisible; move focus to the other step button so keyboard users are never stranded.
    if (focused === prev && index === 0) next?.focus();
    if (focused === next && index === this.#slides.length - 1) prev?.focus();

    // Keep the active thumbnail visible inside its own strip without moving the page.
    const thumb = this.#thumbs[index];
    const strip = thumb?.closest('[data-gallery-thumbs]');
    // The strip is a horizontal row on mobile and a vertical rail on desktop, so centre on whichever axis overflows.
    if (thumb && strip) {
      const behavior = this.#reduced() ? 'auto' : 'smooth';
      if (strip.scrollHeight > strip.clientHeight + 1) {
        strip.scrollTo({ top: thumb.offsetTop - (strip.clientHeight - thumb.offsetHeight) / 2, behavior });
      } else if (strip.scrollWidth > strip.clientWidth + 1) {
        strip.scrollTo({ left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2, behavior });
      }
    }
  }

  #setDisabled(button, disabled) {
    if (!button) return;
    if (disabled) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }

  #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  #reduced() {
    return getComputedStyle(document.documentElement).getPropertyValue('--albra-motion').trim() === 'off';
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
